import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { GlobalFunctions, SnackType } from '@global/GlobalFunctions';
import { LevelAsset, AssetsDataService, ILevelAsset } from '@services/assets-data.service';
import { DatapointsDataService, DataPoint, AssetDataPoint } from '@services/datapoints-data.service';
import { combineLatest, Subject } from 'rxjs';
import { distinctUntilChanged, filter, finalize, takeUntil } from 'rxjs/operators';
import { OrganisationDatapointsComponent } from '../organisation-datapoints/organisation-datapoints.component';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { ConfirmDialogComponent } from 'src/app/dialogs/confirm-dialog/confirm-dialog.component';
import { Account, AccountFeatures, AccountsService, IAccount } from '@services/accounts.service';

export interface EditAssetSettingsDialogData {
	assetItem: LevelAsset;
	selectedAccountUID: string;
	OrganisationUID: string;
	isAdmin?: boolean
}

export enum DataPointToggleOptions {
	ShowOnChart = 0,
	ShowOnTile = 1,
	DisplayDeltaValue = 2
}

@Component({
	selector: 'app-edit-asset-datapoints',
	templateUrl: './edit-asset-datapoints.component.html',
	styleUrls: ['./edit-asset-datapoints.component.scss']
})
export class EditAssetDatapointsComponent implements OnInit {

	/** DataPointToggleOptions */
	public DataPointToggleOptions = DataPointToggleOptions;
	/** AccountFeatures */
	AccountFeatures = AccountFeatures;

	/** Update the Image */
	@Output() imageUpdated = new EventEmitter<any>();

	/** Form Group */
	dialogForm: FormGroup;

	/** Update Image Instantly */
	public numberRND: number = Math.random();
	/** new Image Selected */
	public newImage: boolean;

	/** Data Points */
	public dataPoints: AssetDataPoint[];

	/** Asset Datapoint IDs as a list */
	public dataPointIDs: string[];

	public AssetItem: LevelAsset;

	/** Selected AccountUID */
	public selectedAccountUID: string;

	/** Selected Organisation ID */
	public selectedOrganisationUID: string;

	/** isAdmin */
	public isAdmin: boolean = false;

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** SelectedAccount */
	public SelectedAccount: IAccount;

	/**
	 * Default Constructor
	 * @param fb Form Builder
	 * @param dialogRef Referense to the Dialog box
	 * @param dialogData  object interface
	 * @param dialog 
	 * @param DatapointsDataService DatapointsDataService
	 * @param AssetsDataService 
	 * @param globalFunctions GlobalFunctions
	 * @param siteSubscriptions 
	 */
	constructor(
		private fb: FormBuilder,
		public dialogRef: MatDialogRef<EditAssetDatapointsComponent>,
		public dialog: MatDialog,
		@Inject(MAT_DIALOG_DATA) public dialogData: EditAssetSettingsDialogData,
		private DatapointsDataService: DatapointsDataService,
		private AssetsDataService: AssetsDataService,
		private globalFunctions: GlobalFunctions,
		private siteSubscriptions: siteSubscriptions
	) {
		try {
			combineLatest([
				this.siteSubscriptions.selectedAccount$.pipe(takeUntil(this.finalise), distinctUntilChanged())
			]).pipe(
				// Ensure that subscriptions are not null or undefined
				filter(([selectedAccount]) => !!selectedAccount)
			).subscribe({
				next: ([selectedAccount]) => {
					this.SelectedAccount = selectedAccount;
					this.SelectedAccount = this.siteSubscriptions.normalizeFeatures(this.SelectedAccount) as Account;
				},
				error: (error) => {
					this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
				}
			});
		}
		catch (error) { }
	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {

		this.AssetItem = this.dialogData.assetItem
		this.selectedAccountUID = this.dialogData.selectedAccountUID;
		this.selectedOrganisationUID = this.dialogData.OrganisationUID;
		if (this.dialogData.isAdmin) {
			this.isAdmin = this.dialogData.isAdmin;
		}

		this.dialogForm = this.fb.group({
			AssetID: [this.AssetItem.AssetID || 0],
			Name: [this.AssetItem.Name || '', Validators.required],
			Description: [this.AssetItem.Description || ''],
			ImageURL: [this.AssetItem.ImageURL || ''],
			Expanded: [this.AssetItem.Expanded || false],
			ParentUID: [this.AssetItem.ParentUID || 0],
			Type: [this.AssetItem.Type || 1],
			Watch: [this.AssetItem.Watch || false]
		});

		this.GetDataPoints(this.AssetItem)
	}

	/**
	 * Cancel button for dialog
	 */
	onCancelClick(): void {
		if (this.dialogForm.dirty) {
			this.globalFunctions.UserConfirm$('Lose any unsaved changes?').subscribe(OKResult => {
				if (OKResult) { this.dialogRef.close(); }
				// do nothing if result is false
			});
		} else {
			this.dialogRef.close();
		}
	}

	/**
	 * Edit the Location details
	 * @param imageURL
	 */
	viewImage(imageURL: string): void {
		this.globalFunctions.viewImageFullSize(imageURL);
	}

	/**
	 * Add a Datapoint
	 */
	addDatapoint() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		// Display the dialog box
		const dialogRef = this.dialog.open(OrganisationDatapointsComponent, {
			data: {},
		});

		// Update the original Location with the result
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			if (result["dataPointNames"] != "") {
				// console.log('The dialog was closed');
				if (confirm("Add the Datapoint: " + result["dataPointNames"] + "?")) {
					this.AssetsDataService.putAssetDataPoint$(this.selectedAccountUID,
						this.selectedOrganisationUID, this.AssetItem.AssetID, result["dataPointIDs"]).pipe(
							takeUntil(fetchDataSubject), distinctUntilChanged(),
							filter((sqlResult) => !!sqlResult),
							finalize(() => {
								// Ensure the subscription is terminated and the subject is completed
								fetchDataSubject.next();
								fetchDataSubject.complete();
							})
						).subscribe({
							next: (sqlResult: any) => {
								// console.log(sqlResult)
								if (sqlResult["Error"] != "") {
									this.globalFunctions.DisplaySnackBar({ message: sqlResult["Error"], action: "close", type: SnackType.Error, duration: 0 })
								} else {
									this.globalFunctions.DisplaySnackBar({ message: sqlResult["Result"], action: "" })
								}

								this.GetDataPoints(this.AssetItem)
							},
							error: (error) => {
								if (error.status != 404) {
									this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.putAssetDataPoint$", false);
								}
							},
							//complete: () => { console.log("this.AssetsDataService.putAssetDataPoint$.subscribe: Complete"); }
						});
				}
			}
		});
	}

	/**
	 * Delete the Datapoint
	 * @param dataPoint
	 */
	deleteDataPoint(dataPoint: DataPoint): void {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		if (confirm("Delete the Datapoint: " + dataPoint.PVName + "?")) {
			this.AssetsDataService.deleteAssetDataPoint$(this.selectedAccountUID,
				this.selectedOrganisationUID, this.AssetItem.AssetID, dataPoint.DataPointID).pipe(
					takeUntil(fetchDataSubject), distinctUntilChanged(),
					filter((sqlResult) => !!sqlResult),
					finalize(() => {
						// Ensure the subscription is terminated and the subject is completed
						fetchDataSubject.next();
						fetchDataSubject.complete();
					})
				).subscribe({
					next: (sqlResult: any) => {
						if (sqlResult.Result == "Error") {
							this.globalFunctions.DisplaySnackBar({
								message: sqlResult.Error,
								type: SnackType.Error,
								duration: 1000
							});
						} else {
							this.GetDataPoints(this.AssetItem)
						}
					},
					error: (error) => {
						if (error.status != 404) {
							this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.deleteAssetDataPoint$", false);
						}
					},
					//complete: () => { console.log("this.AssetsDataService.deleteAssetDataPoint$.subscribe: Complete"); }
				});
		}
	}

	/**
	 * patchAssetDataPoint
	 * @param data data 
	 * @param event event 
	 */
	patchAssetDataPoint(data: any, event: MatSlideToggleChange, toggleOption: DataPointToggleOptions) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		let patchAssetDataPoint: AssetDataPoint = data;
		if (patchAssetDataPoint) {
			switch (toggleOption) {
				case (DataPointToggleOptions.ShowOnChart):
					patchAssetDataPoint.ShowOnChart = event.checked;
					break;
				case (DataPointToggleOptions.ShowOnTile):
					patchAssetDataPoint.ShowOnTile = event.checked;
					break;
				case (DataPointToggleOptions.DisplayDeltaValue):
					patchAssetDataPoint.ShowDeltaValue = event.checked;
					break;
			}
			this.AssetsDataService.patchAssetDataPoint$(this.selectedAccountUID, this.selectedOrganisationUID, this.AssetItem.AssetID,
				patchAssetDataPoint.DataPointID, patchAssetDataPoint).pipe(
					takeUntil(fetchDataSubject), distinctUntilChanged(),
					filter((response) => !!response),
					finalize(() => {
						// Ensure the subscription is terminated and the subject is completed
						fetchDataSubject.next();
						fetchDataSubject.complete();
					})
				).subscribe({
					next: (response) => {
						let message: string = "";
						this.numberRND = Math.random()
						this.GetDataPoints(this.AssetItem);
						if (response.Result == "Error") {
							message = response.Error;
						} else {
							switch (toggleOption) {
								case (DataPointToggleOptions.ShowOnChart):
									message = `DataPoint ${event.checked ? "Unhidden on " : "Hidden on "} Chart`;
									break;
								case (DataPointToggleOptions.ShowOnTile):
									this.siteSubscriptions.RefreshTileValues(true);
									message = `DataPoint ${event.checked ? "Unhidden on " : "Hidden on "} Tile`;
									break;
								case (DataPointToggleOptions.DisplayDeltaValue):
									message = `Delta Value ${event.checked ? "Unhidden on " : "Hidden on "} Chart`;
									break;
							}
						}
						this.globalFunctions.DisplaySnackBar({
							message: message,
							type: SnackType.Info,
							duration: 1000
						});
					}
				});
		}
	}

	/**
	 * Get the Data Points for the Asset
	 * @param asset
	 * @param dateStart
	 * @param dateEnd
	 * @returns
	 */
	GetDataPoints(asset: LevelAsset) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.dataPoints = []
		this.AssetsDataService.getAssetDataPoints$(this.selectedAccountUID, this.selectedOrganisationUID, asset.AssetID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((dataPoints) => !!dataPoints),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (dataPoints: any) => {
				try {
					this.dataPoints = dataPoints.filter((x: DataPoint) => x.Enabled == true);
					this.dataPointIDs = dataPoints.map((x: DataPoint) => x.DataPointID);
				} catch (error) {
					this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.getAssetDataPoints.next", false);
				}
			},
			error: (error) => {
				if (error.status != 404) {
					this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.getAssetDataPoints$", false);
				}
			},
			//complete: () => { console.log("this.AssetsDataService.getAssetDataPoints$.subscribe: Complete"); }
		});
	}

	/**
		 * checkFeature
		 * @param feature feature   
		 * @returns 
		 */
	checkFeature(feature: AccountFeatures): boolean {
		if (this.SelectedAccount && this.SelectedAccount.Features) {
			return AccountsService.checkFeature(feature, this.SelectedAccount)
		}
		return false;
	}
}
